import React, { useEffect, useMemo, useState } from "react";
import FaqTab from "./FaqTab";
import InquiryTab from "./InquiryTab";
import ConsultationTab from "./ConsultationTab";
import BoardPostsTab from "../BoardPostsTab";
import {
  BOARD_GROUPS,
  TAB_VIEW_TYPE,
  getBoardTabs,
} from "../../../services/contactCenterService";
import { getI18nLanguageFromLocalStorage } from "../../../helpers/localStorageHelper";
import { getLocalizedField } from "../../../helpers/localizedHelper";
import { slugify } from "../../../helpers/slugHelper";
import { useTranslation } from "react-i18next";

// English fallbacks for this component's common_ns keys. The backend common_ns source
// (Google Sheet -> ContentTranslation) overrides these once the keys are seeded; ko/vi
// are served from there. Keeps the component self-sufficient without a local bundle.
const DEFAULTS = {
  "CONTACT_US_CENTER.TAB.INQUIRY": "1:1 Inquiry",
  "CONTACT_US_CENTER.TAB.CONSULTATION": "Course Consultation",
};

const GROUP = BOARD_GROUPS.CUSTOMER_CENTER;

const SUB_TAB_QUERY_PARAM = "subtab";

// Inquiry & Consultation are submission forms, not board tabs. They carry fixed sort
// orders (3 / 4) and are merged with the tab-managed list tabs, then sorted by order so
// the db tabs (Notices / FAQ / ...) interleave around them.
const FORM_TABS = [
  { key: "INQUIRY", slug: "inquiry", labelKey: "CONTACT_US_CENTER.TAB.INQUIRY", order: 3 },
  { key: "CONSULTATION", slug: "consultation", labelKey: "CONTACT_US_CENTER.TAB.CONSULTATION", order: 4 },
];

const getSubTabFromUrl = () => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get(SUB_TAB_QUERY_PARAM);
};

/** 고객센터 — list tabs from tab management + fixed inquiry/consultation forms. */
const CustomerCenter = ({ onOpenPost }) => {
  const [tabs, setTabs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [active, setActive] = useState(null);
  const language = getI18nLanguageFromLocalStorage();
  const { t: translate } = useTranslation("common_ns", { useSuspense: false });
  const t = (key, options) =>
    translate(key, { defaultValue: DEFAULTS[key] ?? key, ...options });

  useEffect(() => {
    getBoardTabs(GROUP).then((data) => {
      setTabs(data || []);
      setLoaded(true);
    });
  }, []);

  const subTabs = useMemo(
    () =>
      [
        ...tabs.map((tb) => ({
          key: `tab-${tb.id}`,
          slug: slugify(tb.nameEn),
          label: getLocalizedField(tb, "name", language),
          tab: tb,
          order: tb.sortOrder ?? 0,
        })),
        ...FORM_TABS.map((f) => ({
          key: f.key,
          slug: f.slug,
          label: t(f.labelKey),
          order: f.order,
        })),
      ].sort((a, b) => a.order - b.order),
    [tabs, language, t]
  );

  // Resolve the active sub-tab once the db tabs have loaded: prefer ?subtab=<slug>, else the first.
  // Wait for `loaded` — the FORM_TABS keep subTabs non-empty from the first render, so resolving
  // before the db tabs arrive would fall back to the inquiry tab and never match ?subtab=faq.
  useEffect(() => {
    if (!loaded || !subTabs.length || active) return;
    const fromUrl = getSubTabFromUrl();
    const match = subTabs.find((s) => s.slug === fromUrl);
    setActive(match ? match.key : subTabs[0].key);
  }, [loaded, subTabs, active]);

  const selectTab = (item) => {
    setActive(item.key);
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    params.set(SUB_TAB_QUERY_PARAM, item.slug);
    window.history.replaceState(
      window.history.state,
      "",
      `${window.location.pathname}?${params.toString()}${window.location.hash}`
    );
  };

  const renderActive = () => {
    if (active === "INQUIRY") return <InquiryTab />;
    if (active === "CONSULTATION") return <ConsultationTab />;

    const found = subTabs.find((s) => s.key === active && s.tab);
    if (!found) return null;
    const tab = found.tab;
    if (tab.viewType === TAB_VIEW_TYPE.ACCORDION_VIEW) {
      return <FaqTab group={GROUP} tab={tab} />;
    }
    // Customer Center is list-only: no gallery, no view toggle.
    return (
      <BoardPostsTab
        group={GROUP}
        tab={tab}
        defaultView="list"
        allowViewToggle={false}
        trailing="views"
        showTargetBanner
        onOpenPost={onOpenPost}
      />
    );
  };

  return (
    <div className="cuc-panel">
      <div className="cuc-pills">
        {subTabs.map((t) => (
          <button
            key={t.key}
            className={`cuc-pill ${active === t.key ? "is-active" : ""}`}
            onClick={() => selectTab(t)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {renderActive()}
    </div>
  );
};

export default CustomerCenter;
